import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Settings } from "lucide-react"
import { Link } from "react-router"
import { useSettingsTarget, type SettingsTargetId } from "../settingsTargets"

/**
 * Next to a value that comes from Settings, on the surface that uses it:
 * where it is set ("Set in Settings › Supplier inquiries › Attachment"),
 * labelled like the destination, and the way there. In an editor holding
 * unsaved work (the inquiry draft) pass `onOpen`, which opens the setting in
 * a side sheet instead of leaving. Nothing for a user who can't open that
 * settings page.
 */
export function FromSettings({
  target,
  onOpen,
  className,
}: {
  target: SettingsTargetId
  onOpen?: () => void
  className?: string
}) {
  const { translate } = useTranslation()
  const { path, trailKeys, visible } = useSettingsTarget(target)
  if (!visible) return null

  const label = translate("SETTINGS/FROM_SETTINGS", {
    place: trailKeys.map((key) => translate(key)).join(" › "),
  })
  const classes = cn(
    "inline-flex items-center gap-1 text-left text-[11px] text-grey5 underline-offset-2 hover:text-white hover:underline",
    className,
  )
  const content = (
    <>
      <Settings aria-hidden className="h-3 w-3 shrink-0" />
      {label}
    </>
  )

  return onOpen ? (
    <button type="button" onClick={onOpen} className={classes}>
      {content}
    </button>
  ) : (
    <Link to={path} className={classes}>
      {content}
    </Link>
  )
}
